/* eslint-disable react/prop-types */
/* eslint-disable react-native/no-inline-styles */
/*
Author: Granite Bagas
*/

import React, { useRef, useCallback, useMemo, useState } from 'react';
import Toast from 'react-native-toast-message';
import { StyleSheet, View } from 'react-native';
import { useFocusEffect } from '@react-navigation/native';
import { useDispatch, useSelector } from 'react-redux';
import { debounce } from 'lodash';

import * as Layout from '@components/screen/Layout';
import SociollaText from '@components/sociolla/SociollaText';
import Button from '@components/form/Button';
import RenderIf from '@components/global/RenderIf';
import Table from '@components/table/Table';
import ModalUpload from '@components/modal/ModalUpload';

import { isWeb } from '@global/screens';
import { MONTH_NAME_SIMPLE } from '@/constants';
import Colors from '@global/colors';
import tableApi from '@/hooks/tableApi';
import {
  getForecastBySalesTeam,
  getForecastBySalesTeamCount,
  uploadForecaseEstimation,
  updateForecastRow,
} from '@api/ms-order';
import { selectParams, getCurrentParams } from '@/store/params';
import store from '@/store';
import { hasNoAccess, sleep } from '@global/helpers';
import { showPopup } from '@/store/modal';
import { showAlert } from '@/store/global';
import csvTemplate from '@/assets/csv/template-import-forecast-stock';

const generateForecastStockTableStructures = (hasAccessEdit, isHoRole) => {
  const now = new Date();
  const currentMonthIndex = now.getMonth();
  const currentDate = now.getDate();
  const editableStartMonthIndex =
    currentMonthIndex + (currentDate > 10 ? 1 : 0);

  const structures = {
    channel: {
      head: 'Channel',
      body: 'market_type.code',
      rowSpan: 2,
      sticky: true,
      stickyWidth: 100,
    },
    account_group: {
      head: 'Account Group',
      body: 'account.code',
      rowSpan: 2,
      sticky: true,
      stickyWidth: 100,
    },
    product_name: {
      head: 'SKU Name',
      body: 'product.name',
      rowSpan: 2,
      sticky: true,
      stickyWidth: 160,
    },
    reference: {
      head: 'SKU Reference',
      body: 'product.combination.reference',
      rowSpan: 2,
      sticky: true,
      stickyWidth: 100,
    },
  };

  MONTH_NAME_SIMPLE.forEach((monthName, monthIndex) => {
    const monthKey = monthName.toLowerCase();
    let isEditableMonth = monthIndex >= editableStartMonthIndex;
    if (!hasAccessEdit) {
      isEditableMonth = false;
    }

    structures[monthKey] = {
      head: monthName,
      body: monthKey,
      colSpan: 2,
      top: true,
    };
    structures[`${monthKey}_ho`] = {
      head: 'HO',
      body: `months.${monthKey}.est_ho`,
      editable: isEditableMonth && isHoRole,
      checkNullUndefinedOnly: true,
      type: 'currency-number',
    };
    structures[`${monthKey}_sales_team`] = {
      head: 'Sales Team',
      body: `months.${monthKey}.est_sales_team`,
      editable: isEditableMonth && !isHoRole,
      checkNullUndefinedOnly: true,
      type: 'currency-number',
    };
  });

  return structures;
};

const FILTER_NAME = 'forecast-stock-sales-team';

const ForecastStockDetailScreen = (props) => {
  const title = props?.route?.params?.title;
  const year = props?.route?.params?.year;
  const id = props?.route?.params?.id;

  const [searchValue, setSearchValue] = useState('');
  const [list, setList] = useState([]);

  const dispatch = useDispatch();

  const didFetchRef = useRef(false);

  const paramsState = useSelector(selectParams(FILTER_NAME));
  const showImportModalForecastStock = useSelector(
    (state) => state.modal.showImportModalForecastStock,
  );
  const auth = useSelector((state) => state.auth);
  const roleUser = auth?.user?.role.code;

  const hasAccessEdit = !hasNoAccess([
    'superadmin',
    'general',
    'sales_admin',
    'branch_head',
    'sales_supervisor',
  ]);

  const isHoRole = useMemo(() => {
    return ['superadmin', 'general'].includes(roleUser);
  }, [roleUser]);

  const TABLE_STRUCTURES = generateForecastStockTableStructures(
    hasAccessEdit,
    isHoRole,
  );

  const forecastApi = tableApi(
    getForecastBySalesTeamCount,
    getForecastBySalesTeam,
  );

  const doSearch = useCallback(
    async (val) => {
      if (val) {
        setSearchValue(val);
      }
      const params = getCurrentParams(store.getState(), FILTER_NAME);

      const finalParams = { ...params };
      if (finalParams?.filter?.keyword?.$regex) {
        const keyword = finalParams.filter.keyword.$regex;
        finalParams.filter.keyword = keyword;
      }
      finalParams.filter.year = year;
      finalParams.sort = 'market_type.code account.code';

      const res = await forecastApi.request(finalParams, id, false);
      setList(res);
    },
    [year, id],
  );

  const resetData = () => {
    forecastApi.reset();
    setList([]);
  };

  const handleValueChange = useCallback(
    debounce(async ({ data, index, action }) => {
      if (action.name == 'column_editable') {
        try {
          const val = Number(action.value) || 0;
          const keyArr = action.key.split('_');
          const month = keyArr[0];
          const role = keyArr[1] == 'ho' ? 'ho' : 'sales_team';

          const currentMonthIndex = new Date().getMonth();
          const targetMonthIndex = MONTH_NAME_SIMPLE.findIndex(
            (monthName) => monthName.toLowerCase() == month,
          );
          const monthDistance =
            targetMonthIndex > -1
              ? (targetMonthIndex - currentMonthIndex + 12) % 12
              : -1;

          const monthData = data?.months?.[month];
          const estimateKey = `est_${role}`;
          const hasExistingValue = Boolean(
            monthData &&
              Object.prototype.hasOwnProperty.call(monthData, estimateKey) &&
              monthData[estimateKey] !== null &&
              monthData[estimateKey] !== undefined,
          );
          const currentValue = hasExistingValue
            ? Number(monthData[estimateKey]) || 0
            : null;

          if (
            hasExistingValue &&
            monthDistance > -1 &&
            monthDistance <= 3 &&
            val >= currentValue
          ) {
            Toast.show({
              text1:
                'For this month until next 3 months, value must be lower than current value.',
              type: 'error',
            });
            return;
          }

          const payload = {
            [`months.${month}.has_estimation`]: val == 0 ? false : true,
            [`months.${month}.est_${role}`]: val,
          };

          await updateForecastRow(id, data._id, payload);
          await doSearch();
        } catch (error) {
          Toast.show({
            text1: error?.response?.data?.message || 'A system error occurred',
            type: 'error',
          });
        }
      }
    }, 500),
    [id, doSearch],
  );

  const doUpload = async (url, totalRows) => {
    try {
      dispatch(showPopup({ name: 'isLoading', value: true }));
      await uploadForecaseEstimation(id, {
        year,
        url,
        ...(totalRows != null && { total_rows: totalRows }),
      });
      dispatch(
        showPopup({
          name: 'showImportModalForecastStock',
          value: false,
        }),
      );
      await sleep(5000);
      dispatch(showPopup({ name: 'isLoading', value: false }));
      dispatch(
        showAlert({
          show: true,
          isConfirm: false,
          title: 'Information',
          description: 'Upload forecast success',
          onCallback: () => {
            doSearch();
          },
        }),
      );
    } catch (error) {
      console.log('🚀 ~ doUpload ~ error:', error);
    }
  };

  const goBack = () => {
    props.navigation.navigate('ForecastStockScreen');
  };

  useFocusEffect(
    useCallback(() => {
      if (!didFetchRef.current) {
        didFetchRef.current = true;
        resetData();
        doSearch();
      }

      return () => {
        didFetchRef.current = false;

        resetData();
      };
    }, [props.route]),
  );

  return (
    <Layout.Container>
      <RenderIf condition={isWeb}>
        <Layout.Back title="Forecast Stock" onCallback={goBack} />
        <Layout.Title title={title}>
          <View style={styles.submitWrapper}>
            <RenderIf condition={hasAccessEdit}>
              <View style={styles.buttonWrapper}>
                <Button
                  rounded
                  type="default"
                  buttonStyle={styles.buttonStyle}
                  disabledStyle={styles.buttonStyle}
                  title={
                    <SociollaText
                      type="medium"
                      style={{
                        fontSize: 14,
                        color: Colors.black,
                      }}>
                      Upload
                    </SociollaText>
                  }
                  onPress={() => {
                    dispatch(
                      showPopup({
                        name: 'showImportModalForecastStock',
                        value: true,
                      }),
                    );
                  }}
                />
              </View>
            </RenderIf>
          </View>
        </Layout.Title>

        <Table
          name={FILTER_NAME}
          filterName={FILTER_NAME}
          structures={TABLE_STRUCTURES}
          searchKey="keyword"
          searchPlaceholder="Search by reference, name and account"
          searchValue={searchValue}
          data={list}
          loading={false}
          totalData={forecastApi.count}
          skip={paramsState.skip}
          limit={paramsState.limit}
          showSearch={true}
          hasNoHide={true}
          showFilter={false}
          hasCallback={false}
          overflowX={true}
          disablePointer={true}
          onSearch={(val) => doSearch(val)}
          onMapAction={handleValueChange}
        />
      </RenderIf>

      {showImportModalForecastStock ? (
        <ModalUpload
          show={showImportModalForecastStock}
          onClose={() =>
            dispatch(
              showPopup({
                name: 'showImportModalForecastStock',
                value: false,
              }),
            )
          }
          templateUrl={csvTemplate}
          onUpload={doUpload}
          autoUploadS3={true}
          name={'forecast-stock'}
          validHeader={[
            'month',
            'year',
            'channel',
            'account_group',
            'reference',
            'quantity',
          ]}
        />
      ) : (
        false
      )}
    </Layout.Container>
  );
};

const styles = StyleSheet.create({
  submitWrapper: {
    flexDirection: 'row',
  },
  buttonWrapper: {
    marginLeft: 15,
  },
  buttonStyle: {
    width: 160,
  },
});

export default ForecastStockDetailScreen;
